import React, { useState, useEffect, useMemo } from "react";
import { ScrollView, View, StyleSheet, TextInput } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { Menu } from "@/components/Menu";
import { supabase } from "@/lib/supabase-client";
import useAuth from "@/hooks/useAuth";
import { useLocalSearchParams, router } from "expo-router";
import { getEntityPermissions } from "@/lib/permissions";
import { NoPermissionScreen } from "@/components/screens/shared/NoPermissionScreen";
import { useSettingsSearch } from "@/contexts/SettingsSearchContext";

type BillingItem = {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  rate: number | null;
  meta_data: Record<string, any>;
  created_at: string;
};

export default function BillingItemsPage() {
  const { colors } = useTheme();
  const { tenantId, userRoles } = useAuth();
  const permissions = getEntityPermissions(userRoles, "billing_items");
  const params = useLocalSearchParams<{ create?: string }>();

  const [billingItems, setBillingItems] = useState<BillingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { searchValue } = useSettingsSearch("billing_items");

  const filteredBillingItems = useMemo(() => {
    if (!searchValue.trim()) return billingItems;
    const search = searchValue.toLowerCase();
    return billingItems.filter(
      (item) => item.name?.toLowerCase().includes(search) || item.description?.toLowerCase().includes(search),
    );
  }, [billingItems, searchValue]);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<{ name: string; description: string; rate: string }>({
    name: "",
    description: "",
    rate: "",
  });

  const loadBillingItems = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .schema("lists")
        .from("billing_items")
        .select("*")
        .order("name", { ascending: true });

      if (error) throw error;
      setBillingItems(data || []);
    } catch (err: any) {
      setError(err.message || "Failed to load billing items");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBillingItems();
  }, []);

  // Watch for create param changes
  useEffect(() => {
    if (params.create === "true") {
      setShowForm(true);
      setEditingId(null);
      setFormData({ name: "", description: "", rate: "" });
    }
  }, [params.create]);

  // Menu actions
  const handleEdit = (item: BillingItem) => {
    setEditingId(item.id);
    setFormData({
      name: item.name,
      description: item.description || "",
      rate: item.rate !== null ? item.rate.toString() : "variable",
    });
    setShowForm(true);
    setError(null);
  };

  const handleDuplicate = (item: BillingItem) => {
    setEditingId(null);
    setFormData({
      name: item.name,
      description: item.description || "",
      rate: item.rate !== null ? item.rate.toString() : "variable",
    });
    setShowForm(true);
    setError(null);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this billing item?")) return;

    try {
      const { error } = await supabase.schema("lists").from("billing_items").delete().eq("id", id);

      if (error) throw error;
      setBillingItems(billingItems.filter((item) => item.id !== id));
    } catch (err: any) {
      setError(err.message || "Failed to delete billing item");
    }
  };

  // Form submit handler
  const handleSubmit = async () => {
    if (!tenantId || !formData.name.trim()) {
      setError("Name is required");
      return;
    }

    let rateValue: number | null = null;
    if (formData.rate.trim().toLowerCase() !== "variable" && formData.rate.trim() !== "") {
      rateValue = parseFloat(formData.rate);
      if (isNaN(rateValue) || rateValue < 0) {
        setError("Rate must be a positive number or 'variable'");
        return;
      }
    }

    try {
      if (editingId) {
        const { data, error } = await supabase
          .schema("lists")
          .from("billing_items")
          .update({
            name: formData.name.trim(),
            description: formData.description.trim() || null,
            rate: rateValue,
          })
          .eq("id", editingId)
          .select()
          .single();

        if (error) throw error;
        setBillingItems(billingItems.map((item) => (item.id === editingId ? data : item)));
      } else {
        const { data, error } = await supabase
          .schema("lists")
          .from("billing_items")
          .insert([
            {
              id: crypto.randomUUID(),
              name: formData.name.trim(),
              description: formData.description.trim() || null,
              rate: rateValue,
              tenant_id: tenantId,
              meta_data: {},
            },
          ])
          .select()
          .single();

        if (error) throw error;
        setBillingItems([...billingItems, data]);
      }

      setFormData({ name: "", description: "", rate: "" });
      setShowForm(false);
      setEditingId(null);
      setError(null);
      router.setParams({ create: undefined });
    } catch (err: any) {
      setError(err.message || (editingId ? "Failed to update billing item" : "Failed to add billing item"));
    }
  };

  const handleCancel = () => {
    setFormData({ name: "", description: "", rate: "" });
    setShowForm(false);
    setEditingId(null);
    setError(null);
    router.setParams({ create: undefined });
  };

  const itemColor = colors.amber.border;
  const itemBackground = colors.amber.background;

  if (!permissions.read) {
    return <NoPermissionScreen entityName="billing items" />;
  }

  return (
    <ScrollView style={styles.container}>
      <View>
        {loading ? (
          <ThemedText style={styles.centered}>Loading billing items...</ThemedText>
        ) : error ? (
          <View style={styles.errorContainer}>
            <ThemedText type="defaultSemiBold" style={{ color: colors.red.text }}>
              {error}
            </ThemedText>
            <Button text="Retry" icon="refresh" style={{ marginTop: 8 }} onPress={loadBillingItems} />
          </View>
        ) : (
          <View style={styles.cardsContainer}>
            {permissions.create && showForm && !editingId && (
              <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <View style={styles.editForm}>
                  <View style={styles.row}>
                    <TextInput
                      style={[
                        styles.input,
                        styles.flexInput,
                        { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                      ]}
                      placeholder="Name *"
                      placeholderTextColor={colors.placeholder}
                      value={formData.name}
                      onChangeText={(text) => setFormData({ ...formData, name: text })}
                      autoFocus
                    />
                    <TextInput
                      style={[
                        styles.input,
                        styles.rateInput,
                        { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                      ]}
                      placeholder="Rate *"
                      placeholderTextColor={colors.placeholder}
                      value={formData.rate}
                      onChangeText={(text) => setFormData({ ...formData, rate: text })}
                      keyboardType="decimal-pad"
                    />
                  </View>
                  <TextInput
                    style={[
                      styles.input,
                      styles.descriptionInput,
                      { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                    ]}
                    placeholder="Description (optional)"
                    placeholderTextColor={colors.placeholder}
                    value={formData.description}
                    onChangeText={(text) => setFormData({ ...formData, description: text })}
                    multiline
                  />
                  <ThemedText style={{ fontSize: 11, opacity: 0.6 }}>
                    Tip: Enter a number for the rate, or type &apos;variable&apos; for items with no fixed rate
                  </ThemedText>
                  <View style={styles.editActions}>
                    <Button
                      text="Cancel"
                      style={[styles.smallBtn, { borderColor: colors.border }]}
                      textStyle={{ fontSize: 13 }}
                      onPress={handleCancel}
                    />
                    <Button
                      text="Add"
                      icon="plus"
                      iconPosition="right"
                      style={[styles.smallBtn, { backgroundColor: colors.primary }]}
                      textStyle={{ fontSize: 13, color: "white" }}
                      onPress={handleSubmit}
                      enabled={!!formData.name.trim()}
                    />
                  </View>
                </View>
              </View>
            )}

            {filteredBillingItems.map((item) => {
              const isEditing = editingId === item.id;

              return (
                <View key={item.id} style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
                  {isEditing ? (
                    <View style={styles.editForm}>
                      <View style={styles.row}>
                        <TextInput
                          style={[
                            styles.input,
                            styles.flexInput,
                            {
                              borderColor: colors.border,
                              color: colors.text,
                              backgroundColor: colors.backgroundSecondary,
                            },
                          ]}
                          placeholder="Name *"
                          placeholderTextColor={colors.placeholder}
                          value={formData.name}
                          onChangeText={(text) => setFormData({ ...formData, name: text })}
                          autoFocus
                        />
                        <TextInput
                          style={[
                            styles.input,
                            styles.rateInput,
                            {
                              borderColor: colors.border,
                              color: colors.text,
                              backgroundColor: colors.backgroundSecondary,
                            },
                          ]}
                          placeholder="Rate *"
                          placeholderTextColor={colors.placeholder}
                          value={formData.rate}
                          onChangeText={(text) => setFormData({ ...formData, rate: text })}
                          keyboardType="decimal-pad"
                        />
                      </View>
                      <TextInput
                        style={[
                          styles.input,
                          styles.descriptionInput,
                          {
                            borderColor: colors.border,
                            color: colors.text,
                            backgroundColor: colors.backgroundSecondary,
                          },
                        ]}
                        placeholder="Description (optional)"
                        placeholderTextColor={colors.placeholder}
                        value={formData.description}
                        onChangeText={(text) => setFormData({ ...formData, description: text })}
                        multiline
                      />
                      <ThemedText style={{ fontSize: 11, opacity: 0.6 }}>
                        Tip: Enter a number for the rate, or type &apos;variable&apos; for items with no fixed rate
                      </ThemedText>
                      <View style={styles.editActions}>
                        <Button
                          text="Cancel"
                          style={[styles.smallBtn, { borderColor: colors.border }]}
                          textStyle={{ fontSize: 13 }}
                          onPress={handleCancel}
                        />
                        <Button
                          text="Update"
                          icon="check"
                          iconPosition="right"
                          style={[styles.smallBtn, { backgroundColor: colors.primary }]}
                          textStyle={{ fontSize: 13, color: "white" }}
                          onPress={handleSubmit}
                          enabled={!!formData.name.trim()}
                        />
                      </View>
                    </View>
                  ) : (
                    <View style={styles.itemContent}>
                      <View style={styles.itemInfo}>
                        <View style={styles.itemHeader}>
                          <ThemedText type="defaultSemiBold">{item.name}</ThemedText>
                          <View
                            style={[
                              styles.rateBadge,
                              {
                                backgroundColor: itemBackground,
                                borderColor: itemColor,
                              },
                            ]}
                          >
                            <ThemedText style={[styles.rateText, { color: itemColor }]}>
                              {item.rate !== null ? `$${item.rate.toFixed(2)}` : "Variable"}
                            </ThemedText>
                          </View>
                          <Menu
                            buttonStyle={{
                              marginLeft: "auto",
                              alignSelf: "flex-start",
                              borderWidth: 0,
                              backgroundColor: "transparent",
                            }}
                            hideArchive={true}
                            canEdit={permissions.update}
                            canDuplicate={permissions.create}
                            canDelete={permissions.delete}
                            onEdit={() => handleEdit(item)}
                            onDuplicate={() => handleDuplicate(item)}
                            onDelete={() => handleDelete(item.id)}
                          />
                        </View>
                        {item.description && (
                          <ThemedText style={[styles.description, { color: colors.textSecondary }]}>
                            {item.description}
                          </ThemedText>
                        )}
                      </View>
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginTop: 16,
  },
  cardsContainer: {
    gap: 12,
  },
  card: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
    overflow: "hidden",
  },
  itemContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
  },
  itemInfo: {
    flex: 1,
    gap: 4,
  },
  itemHeader: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: 8,
  },
  badgeRow: {
    flexDirection: "row",
    gap: 6,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
  },
  rateBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
  },
  rateText: {
    fontSize: 11,
    fontWeight: "700",
  },
  description: {
    fontSize: 13,
  },
  editForm: {
    padding: 16,
    gap: 12,
  },
  row: {
    flexDirection: "row",
    gap: 8,
  },
  input: {
    height: 40,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  flexInput: {
    flex: 1,
  },
  rateInput: {
    width: 100,
  },
  descriptionInput: {
    height: 60,
    paddingTop: 10,
    textAlignVertical: "top",
  },
  editActions: {
    flexDirection: "row",
    gap: 8,
    justifyContent: "flex-end",
  },
  smallBtn: {
    height: 32,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  centered: {
    textAlign: "center",
    paddingVertical: 32,
    opacity: 0.7,
  },
  errorContainer: {
    alignItems: "center",
    paddingVertical: 32,
  },
});
